export const formatNumberAsUSD = (
    value: number,
    minimumFractionDigits = 0
): string =>
    // @ts-ignore the TS types for Intl are not up to date
    new Intl.NumberFormat(undefined, USDFormat(minimumFractionDigits)).format(
        value
    );

export const USDFormat = (minimumFractionDigits = 0) => ({
    style: 'currency',
    currency: 'USD',
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits,
});

// Returns true if a pending invitation has passed its TTL since creation.
// Expiry is only meaningful when enforcement is on (opts.enforced); otherwise
// invitations never functionally expire, so we never report them as expired.
// opts.ttlDays (global admin setting) takes precedence over the stored value.
export const isInvitationExpired = (
    invitation?: {
        created?: string;
        ttl?: string;
    },
    opts?: { ttlDays?: number; enforced?: boolean }
): boolean => {
    if (opts && opts.enforced === false) return false;
    if (!invitation?.created) return false;
    const created = new Date(invitation.created).getTime();
    if (isNaN(created)) return false;
    const ttlDaysOverride = opts?.ttlDays;
    if (ttlDaysOverride && ttlDaysOverride > 0) {
        const ms = ttlDaysOverride * 24 * 60 * 60 * 1000;
        return Date.now() > created + ms;
    }
    const match = (invitation.ttl || '24h').match(/^(\d+)\s*([hdm]?)$/i);
    const amount = match ? parseInt(match[1], 10) : 24;
    const unit = (match?.[2] || 'h').toLowerCase();
    const ms =
        unit === 'd'
            ? amount * 24 * 60 * 60 * 1000
            : unit === 'm'
              ? amount * 60 * 1000
              : amount * 60 * 60 * 1000;
    return Date.now() > created + ms;
};

export type InvitationDeliveryState =
    | 'delivered'
    | 'pending'
    | 'failed'
    | 'unknown'
    | 'none';

type InvitationLike = {
    status?: number;
    activated?: string | null;
    deleted?: string | null;
    mailgun_id?: string;
    delivery_status?: string;
    delivery_detail?: string;
    created?: string;
    ttl?: string;
};

export const DELIVERY_COLORS: Record<InvitationDeliveryState, string> = {
    delivered: '#4caf50',
    pending: '#f9a825',
    failed: '#e53935',
    unknown: '#aaaaaa',
    none: '#aaaaaa',
};

const classifyOne = (
    inv: InvitationLike
): 'failed' | 'unknown' | 'pending' | 'delivered' => {
    const ds = (inv.delivery_status || '').toLowerCase();
    if (ds === 'failed' || ds === 'rejected') return 'failed';
    if (!inv.mailgun_id) return 'unknown';
    if (ds === 'delivered') return 'delivered';
    return 'pending';
};

const PRIORITY = { failed: 3, unknown: 2, pending: 1, delivered: 0 } as const;

// Worst-case delivery state (+ a representative mailgun_id and expiry) for a school's pending invitations.
export const getSchoolDeliveryState = (
    school?: {
        public?: { invitations?: InvitationLike[] };
    },
    opts?: { ttlDays?: number; enforced?: boolean }
): {
    state: InvitationDeliveryState;
    mailgunId: string | null;
    expired: boolean;
    detail: string | null;
} => {
    const pending = (school?.public?.invitations || []).filter(
        inv => inv?.status === 0 && !inv?.activated && !inv?.deleted
    );
    if (pending.length === 0)
        return { state: 'none', mailgunId: null, expired: false, detail: null };
    const worst = pending
        .map(classifyOne)
        .reduce((a, b) => (PRIORITY[b] > PRIORITY[a] ? b : a));
    const worstInv = pending.find(inv => classifyOne(inv) === worst);
    return {
        state: worst,
        mailgunId: worstInv?.mailgun_id || null,
        expired: pending.some(inv => isInvitationExpired(inv, opts)),
        detail: worstInv?.delivery_detail || null,
    };
};

// Maps a raw Mailgun failure detail to a human-friendly translation key (or null if not recognized).
export const deliveryErrorHint = (detail?: string | null): string | null => {
    if (!detail) return null;
    const d = detail.toLowerCase();
    if (/no such host|no mx|nxdomain|domain not found|host not found/.test(d))
        return 'resources.user.invitation.delivery.errors.bad_domain';
    if (
        /access denied|\b5\.4\.1\b|not authorized|not permitted|blocked using/.test(
            d
        )
    )
        return 'resources.user.invitation.delivery.errors.access_denied';
    if (
        /mailbox.*(not found|unavailable|does not exist)|user unknown|no such user|recipient.*(rejected|not found)|address rejected|\b55[013]\b/.test(
            d
        )
    )
        return 'resources.user.invitation.delivery.errors.no_mailbox';
    if (/quota|mailbox full|over quota|\b552\b/.test(d))
        return 'resources.user.invitation.delivery.errors.mailbox_full';
    if (/hop count exceeded|mail loop|too many hops|\b5\.4\.14\b/.test(d))
        return 'resources.user.invitation.delivery.errors.mail_loop';
    if (/spam|blocked|blacklist|reputation|policy|greylist/.test(d))
        return 'resources.user.invitation.delivery.errors.blocked';
    return null;
};

// Pysyvä toimitusvirhe (domain/MX puuttuu, osoite pysyvästi virheellinen) — samat kuviot kuin backendin
// PERMANENT_ERROR_RE (api/helpers/invitation.js). Access denied / RecipientNotFound EIVÄT ole pysyviä (uusitaan).
const PERMANENT_DELIVERY_RE =
    /does not exist|no such user|user unknown|nosuchuser|address does not exist|user does not exist|recipient not found on this system|not found by smtp address lookup|no mailbox here|mailbox not found|mailbox unavailable|dns domain .* does not exist|infodomainnonexistent|disableduser|is inactive|unrouteable address|no mx for|no such host|host not found|name or service not known|nxdomain/i;

export const isPermanentDeliveryError = (detail?: string | null): boolean =>
    !!detail && PERMANENT_DELIVERY_RE.test(detail.toLowerCase());

export type AutoResendReason =
    | 'disabled'
    | 'not_pending'
    | 'exhausted'
    | 'expired'
    | 'delivered'
    | 'permanent_error';

export type AutoResendState = {
    eligible: boolean;
    reason?: AutoResendReason;
    nextResendAt: Date | null;
    attemptsLeft: number;
};

// Kertoo onko pending-kutsu automaattisen uudelleenlähetyksen piirissä ja milloin seuraava uusinta aikaisintaan.
// Peilaa backendin get_invitations_for_auto_resend -ehtoja (db/repos/auth.js).
export const getAutoResendState = (
    invitation:
        | (InvitationLike & {
              first_sent?: string;
              auto_resend_count?: number;
          })
        | undefined,
    settings?: {
        autoResendEnabled?: boolean;
        autoResendMode?: string;
        autoResendSchedule?: number[];
        ttlDays?: number;
    }
): AutoResendState => {
    const schedule =
        Array.isArray(settings?.autoResendSchedule) &&
        settings!.autoResendSchedule!.length > 0
            ? settings!.autoResendSchedule!
            : [3, 10, 30];
    const count = Number(invitation?.auto_resend_count) || 0;
    const attemptsLeft = Math.max(schedule.length - count, 0);
    const none: AutoResendState = {
        eligible: false,
        nextResendAt: null,
        attemptsLeft,
    };

    if (!invitation) return none;
    if (settings?.autoResendEnabled !== true)
        return { ...none, reason: 'disabled' };
    if (invitation.status !== 0 || invitation.activated || invitation.deleted)
        return { ...none, reason: 'not_pending' };
    if (count >= schedule.length)
        return { ...none, reason: 'exhausted', attemptsLeft: 0 };

    // Alkuperäistä (first_sent) TTL:ää vanhempia ei enää uusita.
    const origin = invitation.first_sent || invitation.created;
    const ttlDays =
        settings?.ttlDays && settings.ttlDays > 0 ? settings.ttlDays : 30;
    if (origin) {
        const originMs = new Date(origin).getTime();
        if (!isNaN(originMs) && Date.now() > originMs + ttlDays * 86400000)
            return { ...none, reason: 'expired' };
    }

    const mode = settings?.autoResendMode === 'all' ? 'all' : 'stuck_failed';
    const ds = (invitation.delivery_status || '').toLowerCase();
    if (mode === 'stuck_failed' && ds === 'delivered')
        return { ...none, reason: 'delivered' };
    if (
        (ds === 'failed' || ds === 'rejected') &&
        isPermanentDeliveryError(invitation.delivery_detail)
    )
        return { ...none, reason: 'permanent_error' };

    // Seuraava uusinta aikaisintaan: viimeisin lähetys (created, nollautuu) + schedule[count] vrk.
    const base = invitation.created
        ? new Date(invitation.created).getTime()
        : NaN;
    const nextMs = isNaN(base) ? NaN : base + schedule[count] * 86400000;
    return {
        eligible: true,
        nextResendAt: isNaN(nextMs) ? null : new Date(nextMs),
        attemptsLeft,
    };
};
